import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import api from '../utils/api';
import toast from 'react-hot-toast';
import ContactActions from '../components/ContactActions';

export default function PaymentReminder() {
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [reminderText, setReminderText] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      const { data } = await api.get('/bookings');
      // Only show bookings with pending balance
      setBookings(data.filter(b => b.pricing.totalPending > 0));
    } catch (err) {
      toast.error('Failed to load bookings');
    }
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    const due = new Date(dueDate);
    return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  };

  const getUrgencyColor = (days) => {
    if (days === null) return 'text-gray-500';
    if (days <= 0) return 'text-red-600';
    if (days <= 2) return 'text-yellow-600';
    if (days <= 5) return 'text-orange-500';
    return 'text-green-600';
  };

  const getUrgencyBadge = (days) => {
    if (days === null) return { text: 'No due date', bg: 'bg-gray-100' };
    if (days <= 0) return { text: `🔴 OVERDUE by ${Math.abs(days)} days`, bg: 'bg-red-100' };
    if (days <= 2) return { text: `🟡 Due in ${days} days`, bg: 'bg-yellow-100' };
    if (days <= 5) return { text: `🟠 Due in ${days} days`, bg: 'bg-orange-100' };
    return { text: `🟢 Due in ${days} days`, bg: 'bg-green-100' };
  };

  const bookingOptions = bookings.map(b => {
    const days = getDaysUntilDue(b.pricing.advanceDueDate);
    return {
      value: b.id,
      label: `${b.customerName} - ${b.trekName} (₹${b.pricing.totalPending?.toLocaleString('en-IN')} pending)`,
      days
    };
  });

  const handleBookingSelect = (selected) => {
    const booking = bookings.find(b => b.id === selected.value);
    setSelectedBooking(booking);
    setReminderText('');
  };

  const generateReminder = async () => {
    if (!selectedBooking) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/documents/payment-reminder/${selectedBooking.id}`);
      setReminderText(data.text);
      toast.success('Payment reminder generated!');
    } catch (err) {
      toast.error('Failed to generate reminder');
    } finally {
      setLoading(false);
    }
  };

  const copyReminder = () => {
    navigator.clipboard.writeText(reminderText);
    toast.success('Reminder copied to clipboard!');
  };

  const days = selectedBooking ? getDaysUntilDue(selectedBooking.pricing.advanceDueDate) : null;
  const urgency = getUrgencyBadge(days);

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">⏰ Payment Reminder</h1>

      {/* Booking Selection */}
      <section className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Select Booking (Pending Payments Only)</h2>
        <Select options={bookingOptions} onChange={handleBookingSelect}
          placeholder="Search bookings with pending balance..." className="text-sm" />

        {selectedBooking && (
          <div className="mt-4">
            <div className="p-4 bg-gray-50 rounded-lg border">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div><span className="text-gray-500">Customer:</span> <strong>{selectedBooking.customerName}</strong></div>
                <div><span className="text-gray-500">Trek:</span> <strong>{selectedBooking.trekName}</strong></div>
                <div><span className="text-gray-500">Dates:</span> <strong>{selectedBooking.startDate} – {selectedBooking.endDate}</strong></div>
                <div><span className="text-gray-500">Pending:</span> <strong className="text-red-600">₹{selectedBooking.pricing.totalPending?.toLocaleString('en-IN')}</strong></div>
                <div><span className="text-gray-500">Due Date:</span> <strong>{selectedBooking.pricing.advanceDueDate || 'Not set'}</strong></div>
                <div>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${urgency.bg} ${getUrgencyColor(days)}`}>
                    {urgency.text}
                  </span>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-200">
                <ContactActions customerName={selectedBooking.customerName} contactNumber={selectedBooking.contactNumber} />
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Reminder Text */}
      {selectedBooking && (
        <section className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800">📝 Reminder Message</h2>
            <div className="space-x-2">
              <button onClick={generateReminder} disabled={loading}
                className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-700 disabled:opacity-50">
                {loading ? 'Generating...' : 'Generate Reminder'}
              </button>
              {reminderText && (
                <button onClick={copyReminder} className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700">
                  📋 Copy to Clipboard
                </button>
              )}
            </div>
          </div>
          {reminderText ? (
            <textarea value={reminderText} onChange={e => setReminderText(e.target.value)}
              className="w-full h-[500px] p-4 border rounded-lg font-mono text-sm bg-gray-50 resize-none" />
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400 border rounded-lg">
              Click "Generate Reminder" to create payment reminder message
            </div>
          )}
        </section>
      )}
    </div>
  );
}
